import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Sparkles, Play, Gift, ShieldCheck, Star, ArrowRight, User, GraduationCap, Heart, X, Download, TrendingUp, CheckCircle, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [childName, setChildName] = useState('');
  const [storyPreview, setStoryPreview] = useState(null);
  const [inputValid, setInputValid] = useState(null); // null, true, or false
  const [showPWA, setShowPWA] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // Static counts / copy (per Updates0119.pdf)
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewForm, setReviewForm] = useState({
    displayName: '',
    relationship: '',
    location: '',
    childName: '',
    childAge: '',
    rating: 5,
    headline: '',
    content: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  // Content toggles (per Updates0119.pdf)
  const SHOW_VALUE_PILLARS_SECTION = false;
  const SHOW_TESTIMONIALS_SECTION = true;
  const SHOW_SHARE_REVIEW_SECTION = true;

  // const statsHighlights = [
  //   { label: 'Families Onboard', value: '12,874+', detail: 'across 42 countries', icon: Users },
  //   { label: 'Avg. Bedtime Rating', value: '4.9 / 5', detail: 'parent satisfaction', icon: Star },
  //   { label: 'Stories Crafted Weekly', value: '18,300+', detail: 'unique adventures', icon: BookOpen },
  //   { label: 'Setup Time', value: '2 min', detail: 'from idea to bedtime', icon: Clock },
  // ];

  const valuePillars = [
    {
      title: 'Hyper-Personalized Journeys',
      description: 'AI-crafted chapters weave in your child’s name, favorite people, and milestones to deepen belonging.',
      icon: Sparkles,
      accent: 'from-purple-50 to-pink-50',
    },
    {
      title: 'Research-backed Learning',
      description: 'Developed with child psychologists to boost reading comprehension, empathy, and emotional vocabulary.',
      icon: GraduationCap,
      accent: 'from-blue-50 to-indigo-50',
    },
    {
      title: 'Family Memories Vault',
      description: 'Save every adventure, record voiceovers, and build a keepsake library you can revisit for years.',
      icon: Gift,
      accent: 'from-yellow-50 to-orange-50',
    },
    {
      title: 'Safe & Secure by Design',
      description: 'COPPA-friendly safeguards, private sharing controls, and 24/7 support keep your stories protected.',
      icon: ShieldCheck,
      accent: 'from-green-50 to-emerald-50',
    },
  ];

  const reviewFallbacks = [
    {
      id: 'fallback-1',
      displayName: 'Priya M.',
      relationship: 'Mom to 6-year-old Aanya',
      location: 'Austin, TX',
      rating: 5,
      headline: 'Astronaut dreams unlocked',
      content: "My daughter asks for her 'astronaut adventure' every single night. She’s reading louder and prouder than ever.",
      badge: 'Reading Confidence ↑ 38%',
      childName: 'Aanya',
      childAge: '6',
    },
    {
      id: 'fallback-2',
      displayName: 'Jason & Lee',
      relationship: 'Parents of Kai & Mina',
      location: 'Toronto, CA',
      rating: 5,
      headline: 'Twins, two journeys',
      content: 'Having twins with different interests was tough—now each gets a custom story and we enjoy bedtime again.',
      badge: 'Twin-approved',
      childName: 'Kai & Mina',
      childAge: '5',
    },
    {
      id: 'fallback-3',
      displayName: 'Elena R.',
      relationship: 'Mom to 4-year-old Mateo',
      location: 'Madrid, ES',
      rating: 5,
      headline: 'Grandma on every page',
      content: 'We record grandma’s voice reading the stories. It’s become our most treasured routine, even when she travels.',
      badge: 'Multilingual Magic',
      childName: 'Mateo',
      childAge: '4',
    },
  ];

  const educatorEndorsements = [
    {
      title: 'Supports Early Literacy Goals',
      detail: 'Story pacing, phonetic repetition, and guided prompts align with K-3 reading standards.',
    },
    {
      title: 'Encourages Social-Emotional Growth',
      detail: 'Personalized narratives reinforce cooperation, courage, and empathy through relatable scenarios.',
    },
    {
      title: 'Inclusive Representation',
      detail: 'Every story adapts names, family structures, and cultural touchpoints to mirror your child’s world.',
    },
  ];

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect device and browser type
  useEffect(() => {
    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(iOS);

    // Check if Safari (but not Chrome on iOS)
    const isChromeOnIOS = /CriOS|FxiOS/.test(navigator.userAgent);
    const isFirefoxOnIOS = /FxiOS/.test(navigator.userAgent);
    const isSafariBrowser = (iOS && !isChromeOnIOS && !isFirefoxOnIOS) ||
      /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
    setIsSafari(isSafariBrowser);

    // Check if app is already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator.standalone === true) ||
      document.referrer.includes('android-app://');
    setIsStandalone(standalone);
  }, []);

  // PWA Installation - Chrome/Edge (Android, iOS Chrome, and Desktop Chrome)
  useEffect(() => {
    // Don't show if already installed
    if (isStandalone) {
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show PWA banner for Chrome/Edge (mobile and desktop)
      // Desktop Chrome also supports PWA installation
      setTimeout(() => setShowPWA(true), 2000); // Show after 2 seconds
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if app is already installed
    window.addEventListener('appinstalled', () => {
      setShowPWA(false);
      setDeferredPrompt(null);
      setIsStandalone(true);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, [isStandalone]);

  // PWA Installation - Safari iOS (manual instructions)
  useEffect(() => {
    // Only show for Safari iOS and not already installed
    if (!isIOS || !isSafari || isStandalone) {
      return;
    }

    // Check if already added to home screen (iOS detection)
    const isInStandaloneMode = window.navigator.standalone === true;
    if (isInStandaloneMode) {
      setIsStandalone(true);
      return;
    }

    // Show for Safari iOS on mobile after delay - every time user visits home page
    if (isMobile) {
      setTimeout(() => setShowPWA(true), 2000); // Show after 2 seconds
    }
  }, [isIOS, isSafari, isStandalone, isMobile]);

  // Fallback: Show for any mobile browser if not already shown (after other handlers)
  useEffect(() => {
    // Wait a bit to see if other handlers set showPWA
    const timer = setTimeout(() => {
      if (!showPWA && !isStandalone && isMobile) {
        // Show the prompt every time user visits (fallback for browsers without beforeinstallprompt)
        // Show for Safari iOS even if beforeinstallprompt didn't fire
        if (isIOS && isSafari && !deferredPrompt) {
          setShowPWA(true);
        }
        // Show for other mobile browsers that don't support beforeinstallprompt
        else if (!isIOS && !deferredPrompt) {
          setShowPWA(true);
        }
      }
    }, 4000); // Wait 4 seconds to let other handlers run first

    return () => clearTimeout(timer);
  }, [showPWA, isStandalone, isSafari, isIOS, deferredPrompt, isMobile]);

  const handleInstallPWA = async () => {
    if (deferredPrompt) {
      // Chrome/Edge installation
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPWA(false);
        setDeferredPrompt(null);
        setIsStandalone(true);
      }
    } else if (isIOS && isSafari) {
      // Safari iOS - just dismiss for current page load, will show again on next visit
      setShowPWA(false);
    }
  };

  const dismissPWA = () => {
    // Dismiss for current page load only - will show again when user visits home page next time
    setShowPWA(false);
  };

  const validateName = (name) => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setInputValid(null);
      return false;
    }
    if (trimmed.length < 2) {
      setInputValid(false);
      return false;
    }
    if (!/^[a-zA-Z\s'-]+$/.test(trimmed)) {
      setInputValid(false);
      return false;
    }
    setInputValid(true);
    return true;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setChildName(value);
    validateName(value);

    // Auto-generate preview when name is valid
    if (validateName(value)) {
      setTimeout(() => {
        if (value.trim() && validateName(value)) {
          setStoryPreview({
            title: `${value.trim()}'s Magical Adventure`,
            text: `In the enchanted kingdom of Storyville, a brave hero named ${value.trim()} discovered a magical book that could grant any wish. As ${value.trim()} opened the book, golden sparkles filled the air, and suddenly, ${value.trim()} was transported to a world where dreams come true!`
          });
        }
      }, 500); // Delay for better UX
    } else {
      setStoryPreview(null);
    }
  };

  const handleCreateStory = () => {
    if (validateName(childName)) {
      handleGetStarted();
    }
  };

  const handleGetStarted = () => {
    // Allow unauthenticated users to start profile creation with child name
    if (childName.trim()) {
      navigate(`/profiles/create?firstName=${encodeURIComponent(childName.trim())}`);
    } else {
      if (isAuthenticated) {
        navigate('/profiles/create');
      } else {
        navigate('/register');
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchReviews = async () => {
      try {
        const { data } = await api.get('/reviews', {
          params: { limit: 6, featured: true },
        });
        if (data.success && isMounted) {
          setReviews(data.reviews || []);
        }
      } catch (error) {
        console.error('Failed to fetch reviews', error);
      } finally {
        if (isMounted) {
          setReviewsLoading(false);
        }
      }
    };
    fetchReviews();
    return () => {
      isMounted = false;
    };
  }, []);

  const displayedReviews = reviews.length > 0 ? reviews : reviewFallbacks;

  const renderReviewStars = (rating = 5) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((idx) => (
        <Star
          key={idx}
          className={`h-4 w-4 ${idx <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
        />
      ))}
    </div>
  );

  const handleReviewInput = (field, value) => {
    setReviewForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.displayName.trim() || reviewForm.content.trim().length < 20) {
      toast.error('Please provide your name and at least 20 characters in your story.');
      return;
    }
    try {
      setSubmittingReview(true);
      await api.post('/reviews', {
        displayName: reviewForm.displayName.trim(),
        relationship: reviewForm.relationship.trim() || null,
        location: reviewForm.location.trim() || null,
        childName: reviewForm.childName.trim() || null,
        childAge: reviewForm.childAge.trim() || null,
        rating: Number(reviewForm.rating),
        headline: reviewForm.headline.trim() || null,
        content: reviewForm.content.trim(),
        source: 'home_form',
      });
      toast.success('Thanks for sharing! We’ll review your story shortly.');
      setReviewForm({
        displayName: '',
        relationship: '',
        location: '',
        childName: '',
        childAge: '',
        rating: 5,
        headline: '',
        content: '',
      });
      setReviews((prev) => prev);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to submit review right now.');
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex flex-col">
      <Header />

      {/* PWA Install Prompt - Bottom Popup (Mobile and Desktop) */}
      {showPWA && !isStandalone && (
        <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up-safe">
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white shadow-2xl border-t-4 border-yellow-400 relative">
            {/* Close button - Top right corner */}
            <button
              onClick={dismissPWA}
              className="absolute top-2 right-2 z-10 text-white/90 hover:text-white hover:bg-white/20 p-2 rounded-full transition-all active:scale-95"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Safari iOS Instructions */}
            {isIOS && isSafari ? (
              <div className="p-4 md:p-5 pr-12">
                <div className="container mx-auto">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="bg-white/20 rounded-full p-2.5 flex-shrink-0">
                      <Download className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-base md:text-lg mb-1">Install MyStoryMe App</h3>
                      <p className="text-sm opacity-95 mb-3">Add to your home screen for quick access and offline stories!</p>

                      {/* Safari-specific instructions */}
                      <div className="bg-white/15 rounded-lg p-3 mb-3 backdrop-blur-sm">
                        <p className="text-xs font-semibold mb-2 flex items-center gap-1">
                          <span className="text-yellow-300">📱</span> How to install:
                        </p>
                        <ol className="text-xs space-y-1.5 ml-5 list-decimal">
                          <li>Tap the <span className="font-bold">Share</span> button <span className="text-yellow-300">(□↑)</span> at the bottom</li>
                          <li>Scroll down and tap <span className="font-bold">"Add to Home Screen"</span></li>
                          <li>Tap <span className="font-bold">"Add"</span> to confirm</li>
                        </ol>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={dismissPWA}
                          className="flex-1 bg-white/25 hover:bg-white/35 text-white border border-white/40 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all active:scale-95"
                        >
                          Got it!
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Chrome/Edge Installation */
              <div className="p-4 md:p-5 pr-12">
                <div className="container mx-auto">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 rounded-full p-2.5 flex-shrink-0">
                      <Download className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-base md:text-lg mb-1">Install MyStoryMe App</h3>
                      <p className="text-sm opacity-95">
                        {isMobile ? 'Get instant access to stories on your phone. Works offline!' : 'Install the app for quick access and offline stories!'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={handleInstallPWA}
                        className="bg-white text-purple-600 hover:bg-gray-100 px-4 py-2.5 rounded-lg text-sm font-bold shadow-lg transition-all active:scale-95 whitespace-nowrap"
                      >
                        Install Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <main className="flex-1">
        <section className="bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex items-center -mt-1 pb-8 md:pb-12">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8 lg:mb-12">
              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight mb-4 lg:mb-6">
                Spark Your Child's{' '}
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Imagination
                </span>
                {' '}With Stories About Them!
              </h1>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-6 lg:mb-8">
                Personalized, on-screen stories created just
                for your child — their name, personality, and
                world built right in.
              </p>
            </div>

            {/* Three Column Layout */}
            <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
              {/* Interactive Demo Section */}
              <div className="lg:col-span-1 order-1">
                <div className="relative">
                  <div className="bg-gradient-to-br from-purple-400 to-pink-400 rounded-3xl p-4 lg:p-6 shadow-2xl transform -rotate-1 hover:rotate-0 transition-transform duration-300 h-full flex">
                    <div className="bg-white border-0 shadow-none h-full w-full transform rotate-1 rounded-2xl p-4 lg:p-5 flex flex-col">
                      <div className="pb-3">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="flex items-center gap-2 text-purple-800 text-lg lg:text-xl font-bold">
                            <Sparkles className="h-5 w-5 animate-spin" />
                            Create
                          </h3>
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full animate-pulse font-semibold">
                            FREE
                          </span>
                        </div>
                        <p className="text-purple-600 text-sm font-medium">
                          See their name come to life in 30 seconds
                        </p>
                      </div>

                      <div className="space-y-4 flex-1 flex flex-col">
                        {/* Name Input with Validation */}
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-purple-800">Your Child's Name</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={childName}
                              onChange={handleNameChange}
                              onBlur={() => validateName(childName)}
                              placeholder="Your child's name..."
                              className={`w-full pr-12 border-2 text-base lg:text-lg font-medium rounded-md px-3 py-2 transition-all ${inputValid === null
                                  ? 'border-purple-200 focus:border-purple-500'
                                  : inputValid
                                    ? 'border-green-400 bg-green-50 focus:border-green-500'
                                    : 'border-red-400 bg-red-50 focus:border-red-500'
                                }`}
                            />
                            {inputValid === true && (
                              <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
                            )}
                            {inputValid === false && (
                              <X className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                            )}
                          </div>
                          <button
                            onClick={handleCreateStory}
                            disabled={!inputValid}
                            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 lg:px-6 py-2 font-bold shadow-lg transform hover:scale-105 transition-all duration-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                          >
                            👉 Create Your Child's Story
                          </button>
                          {inputValid === false && (
                            <p className="text-red-500 text-xs">Please enter a valid name (2+ letters)</p>
                          )}
                          {inputValid === true && (
                            <p className="text-green-600 text-xs flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              Great! Ready to create magic
                            </p>
                          )}
                        </div>

                        {/* Progress Indicators */}
                        <div className="bg-purple-50 rounded-lg p-3">
                          <div className="flex items-center justify-between text-xs text-purple-700 mb-2">
                            <span className="font-medium">Your Journey</span>
                            <span className="font-bold">{storyPreview ? '50%' : '0%'}</span>
                          </div>
                          <div className="w-full bg-purple-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: storyPreview ? '50%' : '0%' }}
                            ></div>
                          </div>
                          <div className="flex justify-between mt-2 text-xs text-purple-600">
                            <span className={storyPreview ? 'font-bold' : ''}>1. Name ✓</span>
                            <span className={storyPreview ? 'font-bold' : 'opacity-50'}>2. Profile</span>
                            <span className="opacity-50">3. Story</span>
                          </div>
                        </div>

                        {/* Social Proof */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200">
                          <div className="text-sm font-bold text-blue-800 leading-snug">
                            Their name, personality, and world built right
                            <br />
                            into the story!
                          </div>
                        </div>

                        {/* Dynamic Story Preview */}
                        {storyPreview && (
                          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border-2 border-purple-200 shadow-lg animate-fade-in">
                            <div className="flex items-start gap-3 mb-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-yellow-300 to-orange-300 rounded-full flex items-center justify-center shadow-md">
                                <span className="text-sm">📚</span>
                              </div>
                              <div className="flex-1">
                                <h4 className="font-bold text-purple-800 text-base mb-2">{storyPreview.title}</h4>
                                <p className="text-gray-700 text-sm leading-relaxed mb-3 line-clamp-3">
                                  {storyPreview.text}
                                </p>
                                <div className="flex gap-2">
                                  <button
                                    onClick={handleGetStarted}
                                    className="bg-purple-600 hover:bg-purple-700 text-white flex-1 px-3 py-2 rounded text-sm font-medium transition-colors"
                                  >
                                    <Play className="h-3 w-3 mr-1 inline" />
                                    Listen Now
                                  </button>
                                  <button
                                    onClick={handleGetStarted}
                                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white flex-1 font-bold px-3 py-2 rounded text-sm transition-all"
                                  >
                                    <Sparkles className="h-3 w-3 mr-1 inline" />
                                    Get Full Story
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Video Content Area */}
              <div className="lg:col-span-1 order-3 lg:order-2">
                <div className="relative">
                  <div className="bg-gradient-to-br from-purple-400 to-pink-400 rounded-3xl p-4 lg:p-6 shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-300 h-full flex">
                    <div className="bg-white rounded-2xl p-4 text-center transform -rotate-1 w-full flex flex-col">
                      {/* Video Placeholder with Play Button */}
                      {/* <div className="relative mb-4 group cursor-pointer">
                        <div className="w-full h-32 lg:h-48 bg-gradient-to-br from-purple-100 via-pink-100 to-yellow-100 rounded-xl flex items-center justify-center border-2 border-purple-200 overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 to-pink-400/20"></div>
                          <button className="relative z-10 bg-white/90 hover:bg-white rounded-full p-4 shadow-xl transform group-hover:scale-110 transition-all duration-300">
                            <Play className="h-8 w-8 text-purple-600 fill-purple-600" />
                          </button>
                        </div>
                        <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                          Demo Video
                        </div>
                      </div> */}

                      {/* Child Reaction Elements */}
                      <div className="space-y-3 flex-1 flex flex-col">
                        {/* <div className="flex justify-center gap-2 mb-2">
                          <div className="w-12 h-12 bg-gradient-to-br from-yellow-200 to-orange-200 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                            <span className="text-2xl">😍</span>
                          </div>
                          <div className="w-12 h-12 bg-gradient-to-br from-pink-200 to-red-200 rounded-full flex items-center justify-center shadow-lg animate-bounce" style={{ animationDelay: '0.2s' }}>
                            <span className="text-2xl">🤩</span>
                          </div>
                          <div className="w-12 h-12 bg-gradient-to-br from-purple-200 to-blue-200 rounded-full flex items-center justify-center shadow-lg animate-bounce" style={{ animationDelay: '0.4s' }}>
                            <span className="text-2xl">✨</span>
                          </div>
                        </div> */}

                        {/* Social Proof Quote */}
                        {/* <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-3 border border-yellow-200">
                          <p className="text-gray-800 font-bold text-base lg:text-lg mb-1">
                            "That's MY name in the story!"
                          </p>
                          <p className="text-xs text-gray-600">— Real child reaction</p>
                        </div> */}

                        {/* Badges */}
                        {/* <div className="flex flex-wrap justify-center gap-2">
                          <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full font-medium">
                            🏆 Top Rated
                          </span>
                          <span className="bg-pink-100 text-pink-700 text-xs px-2 py-1 rounded-full font-medium">
                            ⭐ #1 Kids' Choice
                          </span>
                        </div> */}

                        {/* Context Explanation */}
                        <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl p-4 border border-blue-200 text-left shadow-sm flex-1 flex flex-col">
                          <div className="flex items-start gap-2">
                            <Heart className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="w-full">
                              <p className="text-[11px] font-semibold text-blue-900 tracking-[0.22em] mb-2">WHAT MAKES THIS DIFFERENT</p>
                              <p className="text-sm font-bold text-blue-900 leading-snug">
                                Not Just a Story — A Moment That Belongs to Them
                              </p>
                              <p className="text-xs text-blue-700 leading-relaxed mt-2">
                                Most stories talk at children. mySTORYme stories are about them. Your child becomes the main character —
                                making choices, facing challenges, and discovering their own strengths in a story built around who they are.
                              </p>

                              <div className="mt-3 text-xs text-blue-700 space-y-1.5">
                                <div className="flex gap-2">
                                  <span className="mt-0.5">•</span>
                                  <span>Personalized with your child’s name and traits</span>
                                </div>
                                <div className="flex gap-2">
                                  <span className="mt-0.5">•</span>
                                  <span>Designed to be enjoyed on phones, tablets, or laptops</span>
                                </div>
                                <div className="flex gap-2">
                                  <span className="mt-0.5">•</span>
                                  <span>Created to turn bedtime into a shared moment, not a struggle</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={handleGetStarted}
                            className="mt-4 w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-3 py-2.5 rounded-lg text-sm font-bold transition-all shadow-md"
                          >
                            👉 Start Their Story
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Call-to-Actions */}
              <div className="lg:col-span-1 order-2 lg:order-3">
                <div className="relative">
                  <div className="bg-gradient-to-br from-purple-400 to-pink-400 rounded-3xl p-4 lg:p-6 shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-300 h-full flex">
                    <div className="bg-white rounded-2xl p-4 lg:p-5 transform -rotate-1 w-full flex flex-col">
                      <div className="text-center mb-4">
                        <p className="uppercase text-xs tracking-[0.35em] text-purple-500 font-semibold">
                          HOW IT WORKS
                        </p>
                      </div>

                      <div className="space-y-3 flex-1">
                        <div className="bg-gradient-to-br from-white to-purple-50 rounded-xl border border-purple-100 p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-extrabold flex items-center justify-center shadow-md">
                              1
                            </div>
                            <div className="flex-1">
                              <p className="text-lg font-bold text-gray-900">Tell Us About Your Child</p>
                              <p className="text-sm text-gray-600 mt-1">
                                Name, interests, personality — it takes just a minute.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-white to-purple-50 rounded-xl border border-purple-100 p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-extrabold flex items-center justify-center shadow-md">
                              2
                            </div>
                            <div className="flex-1">
                              <p className="text-lg font-bold text-gray-900">We Create Their Story</p>
                              <p className="text-sm text-gray-600 mt-1">
                                A unique, personalized story generated just for them.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-white to-purple-50 rounded-xl border border-purple-100 p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-extrabold flex items-center justify-center shadow-md">
                              3
                            </div>
                            <div className="flex-1">
                              <p className="text-lg font-bold text-gray-900">Read or Listen on Any Device</p>
                              <p className="text-sm text-gray-600 mt-1">
                                Enjoy the story together on mobile, tablet, or desktop — anytime.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="pt-4 mt-auto">
                        <button
                          onClick={handleGetStarted}
                          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 lg:py-4 text-base lg:text-lg font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 rounded-md"
                        >
                          👉 Create a Story in Minutes
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Stats Section */}
        {/* <section className="bg-gradient-to-r from-purple-50 via-white to-pink-50 py-10 lg:py-12 border-y border-purple-100">
          <div className="container mx-auto px-4">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {statsHighlights.map(({ label, value, detail, icon: Icon }) => (
                <div
                  key={label}
                  className="flex items-center gap-4 bg-white/80 rounded-2xl p-4 shadow-lg border border-white/40"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center text-purple-700">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                    <p className="text-sm font-semibold text-purple-700">{label}</p>
                    <p className="text-xs text-gray-500">{detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section> */}

        {/* How It Works Section */}
        {/* <section className="bg-white py-12 lg:py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8 lg:mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                HOW IT WORKS
              </h2>
              <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                Step 1 Tell Us About Your Child • Step 2 We Create Their Story • Step 3 Read or Listen on Any Device
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              <div className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-gradient-to-br from-white to-gray-50 rounded-lg p-6">
                <div className="text-center pb-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 mx-auto flex items-center justify-center text-white mb-4 shadow-lg">
                    <User className="h-8 w-8" />
                  </div>
                  <div className="text-3xl font-bold text-purple-600 mb-3">1</div>
                  <h3 className="text-xl mb-2 font-bold">Tell Us About Your Child</h3>
                </div>
                <p className="text-gray-600 text-center leading-relaxed">
                  Name, interests, personality — it takes just a minute.
                </p>
              </div>

              <div className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-gradient-to-br from-white to-gray-50 rounded-lg p-6">
                <div className="text-center pb-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 mx-auto flex items-center justify-center text-white mb-4 shadow-lg">
                    <BookOpen className="h-8 w-8" />
                  </div>
                  <div className="text-3xl font-bold text-blue-600 mb-3">2</div>
                  <h3 className="text-xl mb-2 font-bold">We Create Their Story</h3>
                </div>
                <p className="text-gray-600 text-center leading-relaxed">
                  A unique, personalized story generated just for them.
                </p>
              </div>

              <div className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-gradient-to-br from-white to-gray-50 rounded-lg p-6">
                <div className="text-center pb-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-pink-600 mx-auto flex items-center justify-center text-white mb-4 shadow-lg">
                    <Sparkles className="h-8 w-8" />
                  </div>
                  <div className="text-3xl font-bold text-pink-600 mb-3">3</div>
                  <h3 className="text-xl mb-2 font-bold">Read or Listen on Any Device</h3>
                </div>
                <p className="text-gray-600 text-center leading-relaxed">
                  Enjoy the story together on mobile, tablet, or desktop — anytime.
                </p>
              </div>
            </div>

            <div className="text-center mt-10">
              <button
                onClick={handleGetStarted}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
              >
                👉 Create a Story in Minutes <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section> */}

        {/* Value Pillars Section */}
        {SHOW_VALUE_PILLARS_SECTION && (
        <section className="bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900 py-14 lg:py-20 text-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-10">
              <div>
                <p className="uppercase text-xs tracking-[0.4em] text-white/70">Why parents love us</p>
                <h2 className="text-3xl lg:text-4xl font-bold mt-3 leading-tight">
                  Personalized storytelling that feels hand-crafted for your family
                </h2>
              </div>
              <button
                onClick={handleGetStarted}
                className="inline-flex items-center gap-2 bg-white text-purple-700 px-5 py-3 rounded-full font-semibold shadow-2xl hover:-translate-y-0.5 transition-transform"
              >
                Explore the wizard <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {valuePillars.map(({ title, description, icon: Icon, accent }) => (
                <div
                  key={title}
                  className={`relative overflow-hidden rounded-3xl p-6 border border-white/10 bg-gradient-to-br ${accent}`}
                >
                  <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff40,_transparent_55%)]" />
                  <div className="relative flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/70 flex items-center justify-center text-purple-700 shadow-lg">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                      <p className="text-sm text-gray-700 mt-2 leading-relaxed">{description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

        {/* Testimonials Section */}
        {SHOW_TESTIMONIALS_SECTION && (
        <section className="bg-white py-12 lg:py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <p className="uppercase text-xs tracking-[0.5em] text-purple-500">Loved by families</p>
              <h2 className="text-3xl font-bold text-gray-900 mt-3 mb-3">Bedtime stories they ask for again</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Thousands of nightly rituals have started right here. Hear how MyStoryMe is transforming family time.
              </p>
            </div>

            {reviewsLoading ? (
              <div className="grid gap-6 md:grid-cols-3">
                {[1, 2, 3].map((skeleton) => (
                  <div
                    key={skeleton}
                    className="rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white p-6 shadow-lg animate-pulse"
                  >
                    <div className="h-6 bg-purple-100 rounded w-32 mb-4" />
                    <div className="h-4 bg-gray-100 rounded w-full mb-3" />
                    <div className="h-4 bg-gray-100 rounded w-5/6 mb-2" />
                    <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
                    <div className="h-4 bg-gray-100 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-3">
                {displayedReviews.map((review) => (
                  <div
                    key={review.id || review.displayName}
                    className="rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white p-6 shadow-lg flex flex-col gap-4"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-semibold text-gray-900">
                          {review.displayName || review.parent}
                        </p>
                        <p className="text-xs text-gray-500">
                          {review.relationship || review.child}
                        </p>
                      </div>
                      {renderReviewStars(review.rating)}
                    </div>
                    {(review.badge || review.featured) && (
                      <span className="inline-flex items-center text-xs font-semibold text-purple-700 bg-purple-100 rounded-full px-3 py-1 w-fit">
                        <Sparkles className="h-3 w-3 mr-1" />
                        {review.badge || 'Featured Parent'}
                      </span>
                    )}
                    {review.headline && (
                      <h3 className="text-lg font-bold text-gray-900">{review.headline}</h3>
                    )}
                    <p className="text-gray-800 text-base leading-relaxed">
                      “{review.content || review.quote}”
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-auto">
                      {review.location && (
                        <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded-full border border-purple-100">
                          <MapPin className="h-3 w-3" />
                          {review.location}
                        </span>
                      )}
                      {(review.childName || review.childAge || review.child) && (
                        <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded-full border border-purple-100">
                          <User className="h-3 w-3" />
                          {review.childName || review.child}
                          {review.childAge ? ` • ${review.childAge}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
        )}

        {/* Share Review Section */}
        {SHOW_SHARE_REVIEW_SECTION && (
        <section className="bg-gradient-to-br from-purple-50 via-white to-pink-50 py-12 lg:py-16 border-t border-purple-100">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-10 items-start">
              <div>
                <p className="uppercase text-xs tracking-[0.4em] text-purple-500 mb-3">Share your magic</p>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  Tell other families how MyStoryMe transformed bedtime
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  We spotlight real parent stories to help new families see what’s possible. Add your voice, highlight
                  your child’s favorite moment, and inspire someone else’s nightly ritual.
                </p>
                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center text-purple-600 font-bold">
                      1
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Keep it personal</p>
                      <p className="text-sm text-gray-500">Mention their name, age, or the adventure that stuck.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center text-purple-600 font-bold">
                      2
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Share the impact</p>
                      <p className="text-sm text-gray-500">Did reading confidence grow? Siblings bond? Tell us!</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center text-purple-600 font-bold">
                      3
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">We’ll reach out</p>
                      <p className="text-sm text-gray-500">Approved stories may appear on the homepage or newsletter.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-2xl border border-purple-100 p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-sm uppercase tracking-[0.3em] text-purple-400">Submit a review</p>
                    <h3 className="text-2xl font-bold text-gray-900 mt-2">Your story matters</h3>
                  </div>
                  <div className="flex gap-1">{renderReviewStars(reviewForm.rating)}</div>
                </div>
                <form className="space-y-4" onSubmit={handleSubmitReview}>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1 block">Your name *</label>
                      <input
                        type="text"
                        value={reviewForm.displayName}
                        onChange={(e) => handleReviewInput('displayName', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        placeholder="Priya M."
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1 block">Relationship</label>
                      <input
                        type="text"
                        value={reviewForm.relationship}
                        onChange={(e) => handleReviewInput('relationship', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        placeholder="Mom to 6-year-old Aanya"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1 block">Location</label>
                      <input
                        type="text"
                        value={reviewForm.location}
                        onChange={(e) => handleReviewInput('location', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        placeholder="Austin, TX"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1 block">Child name(s)</label>
                      <input
                        type="text"
                        value={reviewForm.childName}
                        onChange={(e) => handleReviewInput('childName', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        placeholder="Aanya"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1 block">Child age</label>
                      <input
                        type="text"
                        value={reviewForm.childAge}
                        onChange={(e) => handleReviewInput('childAge', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        placeholder="6"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1 block">Rating</label>
                      <select
                        value={reviewForm.rating}
                        onChange={(e) => handleReviewInput('rating', Number(e.target.value))}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none bg-white"
                      >
                        {[5, 4, 3, 2, 1].map((value) => (
                          <option key={value} value={value}>
                            {value} Stars
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1 block">Headline</label>
                      <input
                        type="text"
                        value={reviewForm.headline}
                        onChange={(e) => handleReviewInput('headline', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        placeholder="Confidence skyrocketed!"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Story *</label>
                    <textarea
                      value={reviewForm.content}
                      onChange={(e) => handleReviewInput('content', e.target.value)}
                      rows={5}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                      placeholder="Share your favorite moment, how your child reacted, or why you recommend MyStoryMe..."
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum 20 characters. Pending reviews are approved within 1–2 days.</p>
                  </div>

                  <button
                    type="submit"
                    disabled={submittingReview}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-xl font-semibold shadow-lg disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  >
                    {submittingReview ? 'Sending your story...' : 'Submit your review'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Educator Endorsements */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-12 lg:py-16 border-t border-blue-100">
          <div className="container mx-auto px-4">
            <div className="lg:flex lg:items-center lg:justify-between mb-8">
              <div>
                <p className="uppercase text-xs tracking-[0.4em] text-blue-500">Educator approved</p>
                <h2 className="text-3xl font-bold text-gray-900 mt-3">Built with teachers & reading coaches</h2>
                <p className="text-gray-600 mt-3 max-w-2xl">
                  We collaborate with literacy experts to ensure every adventure is fun, age-appropriate, and rooted in skills that matter.
                </p>
              </div>
              <div className="mt-6 lg:mt-0">
                <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-blue-100 shadow-sm text-sm font-semibold text-blue-700">
                  <ShieldCheck className="h-4 w-4 text-green-600" />
                  Backed by certified educators
                </div>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {educatorEndorsements.map(({ title, detail }) => (
                <div key={title} className="bg-white rounded-2xl border border-blue-100 p-6 shadow-md">
                  <div className="flex items-center gap-2 text-blue-600 font-semibold text-sm mb-3">
                    <CheckCircle className="h-4 w-4" />
                    Expert insight
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Home;